import { useEffect, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";
import { useToast } from "@chakra-ui/react";
import io, { Socket } from "socket.io-client";
import { DefaultEventsMap } from "@socket.io/component-emitter";
import { ApiAuth, ApiCall } from "../_services/api";
import { IResponse } from "../_services/interface/user.interface";
import useLoginFormStore from "../store/useLoginFormStore";
import useLoginToken from "../store/login.store";
import { CustomAlert } from "../components/Alert/CustomAlert";
import { AppShell } from "./AppShell";
import { clearSessionToken, logAuthDiag, readSessionFallback } from "../_services/authClient";
// GEN-3167: um link para uma tela específica não pode virar "caiu no Dashboard" depois do login.
import { rememberReturnTo } from "../_services/returnTo";

/**
 * Pathless route element for the authenticated area. Handles auth/validation and
 * socket bootstrap. While the user is not validated it renders nothing (so the
 * child routes/Outlet never mount). Once validated it hands the shared state off
 * to <AppShell/>, which provides the contexts and renders the Sidebar + Outlet.
 */
export default function AppLayout() {
  const { setGuid_user, setGuid_company, setGuid_collaborator, setValidate } = useLoginFormStore();
  // useCookies retorna [cookies, setCookie, removeCookie] — o remove é o TERCEIRO.
  // Desestruturar só dois elementos pegava o setCookie no lugar do removeCookie, então
  // o cookie `token` nunca era de fato apagado: no 401 do /auth/validate o app ia pra
  // /login, mas o LoginForm via o token ainda presente e reencaminhava pra "/",
  // gerando o loop / <-> /login (ver Logout.tsx, que já usa o padrão correto).
  const [cookies, , removeCookie] = useCookies(["token"]);
  const [validationResponse, setValidationResponse] = useState<IResponse>();
  const navigate = useNavigate();
  const [updateUserData, setUpdateUserData] = useState<boolean>(false);
  const [socket, setSocket] = useState<Socket<DefaultEventsMap, DefaultEventsMap> | undefined>();
  // deep-review v1 (P3): guarda a ÚLTIMA conexão entre invocações de ValidateUser — o `let socket`
  // local era sempre undefined no momento do close(), então cada revalidação (ex.: editar o próprio
  // perfil) abria uma conexão WS nova sem fechar a anterior (vazamento acumulado na sessão).
  const socketRef = useRef<Socket<DefaultEventsMap, DefaultEventsMap> | undefined>(undefined);
  const toast = useToast();

  const ValidateUser = async (completeWarning = false) => {
    // ApiAuth() monta o Authorization a partir das fontes de token (cookie ->
    // localStorage -> memória). O argumento são HEADERS EXTRAS: passar o token
    // aqui espalhava a string em headers numéricos ("0","1","2"...) — corrigido.
    const resp = await new ApiCall("/auth/validate", ApiAuth()).get();

    const initWebSocket = (guid_user: string) => {
      socketRef.current?.close();

      const s = io(`${process.env.REACT_APP_WEBSOCKET_URL}`, {
        query: {
          guid_user: guid_user,
        },
      });

      socketRef.current = s;
      setSocket(s);
    };

    if (resp.status !== 200) {
      // GEN-3199: sem o motivo da falha ficamos no escuro quando não há status
      // HTTP (bloqueio local). `message` vem do buildErrorResponse (axios err.message).
      logAuthDiag("app-layout:validate-failed", {
        status: resp.status ?? undefined,
        failureReason: (resp.response as { message?: string } | undefined)?.message,
      });
      removeCookie("token", { path: "/" });
      // sessão morta não pode sobreviver no fallback nem na memória (senão o
      // LoginForm/gate a vê e reencaminha pra "/", recriando o loop / <-> /login)
      clearSessionToken();
      useLoginToken.getState().setToken(null);
      // GEN-3167: guarda ONDE o usuário estava — a sessão expirou no meio do caminho, então depois
      // de logar de novo ele volta para a mesma tela em vez de recomeçar pelo Dashboard.
      rememberReturnTo(`${window.location.pathname}${window.location.search}${window.location.hash}`);
      //Navigate the user back to the login page
      navigate("/login");
    } else if (resp.status === 200) {
      const response = resp.response as IResponse;
      const companies = response.companies.map((value) => value.guid_company);
      const collaborator = response.companies.map((value) => value.guid_collaborator);

      if (process.env.REACT_APP_WEBSOCKET_URL) {
        initWebSocket(response.guid_user);
      }

      //Check if any company needs to have their registration finished
      const companyToComplete = response.companies.find((x) => !x.isRegFinished);
      if (companyToComplete) {
        if (completeWarning) {
          toast({
            isClosable: true,
            duration: null,
            position: "top-right",
            containerStyle: {
              maxW: "800px",
            },
            render: ({ onClose }) => (
              <>
                <CustomAlert
                  closableFunc={onClose}
                  descriptionHtml={`Para que o sistema comece buscando informações de sua empresa ${companyToComplete.name} é necessário concluir o cadastro.`}
                  clicable={{
                    action: () => {
                      navigate("/empresas");
                      onClose();
                    },
                    text: "Concluir Cadastro",
                  }}
                  icon={false}
                  status={"info"}
                  variant={"alert"}
                  type={"three"}
                  className={"alert-instance"}
                />
              </>
            ),
          });
        }
      }

      setValidationResponse(response);
      setValidate(response);
      setGuid_user(response.guid_user);
      setGuid_company(companies);
      setGuid_collaborator(collaborator);
    }
  };

  useEffect(() => {
    // Fontes de sessão em ordem (GEN-3111): cookie -> localStorage -> memória (zustand).
    // A memória cobre navegadores que bloqueiam TODO o armazenamento do site (cookie E
    // localStorage) e sobrevive à navegação interna /login -> / (sem reload).
    const memoryToken = useLoginToken.getState().token;
    const hasSession = Boolean(cookies.token || readSessionFallback() || memoryToken);
    if (!hasSession) {
      // AUTH_DIAG: chegamos na área autenticada SEM sessão alguma. Se isto aparecer logo
      // após um login (SSO/senha), é o sinal de que a persistência falhou no navegador.
      logAuthDiag("app-layout:mount-without-cookie");
      // GEN-3167: é ESTE o caminho de quem abre um link recebido sem sessão (CTA de e-mail, link de
      // um colega). Sem guardar o destino, o login joga a pessoa no Dashboard e o link não serviu
      // para nada.
      rememberReturnTo(`${window.location.pathname}${window.location.search}${window.location.hash}`);
      //Send the person back to the login page
      removeCookie("token", { path: "/" });
      navigate("/login");
    } else {
      logAuthDiag("app-layout:mount-with-cookie", {
        viaFallback: !cookies.token && !!readSessionFallback(),
        viaMemory: !cookies.token && !readSessionFallback() && !!memoryToken,
      });
      ValidateUser(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //This hook is used when the main user is changed on the system and his data need to be updated
  useEffect(() => {
    if (updateUserData) {
      ValidateUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateUserData]);

  if (!validationResponse) {
    //TODO inserir um tipo de Loading aqui
    return null;
  }

  return <AppShell validation={validationResponse} socket={socket} onUpdate={setUpdateUserData} />;
}
